import { useEffect, useState } from 'react'
import { fetchSession } from './googleSession'
import { onSignedOut } from './sessionSignal'
import { SignInGate } from './SignInGate'
import { chooseRelayTarget } from '../transport/relay'
import { connectStream } from '../servers/commandStream'
import { bootstrapChatv1, teardownChatv1 } from '../chatv1/bootstrap'

/**
 * Nothing mounts until we know who is asking.
 *
 * The gate used to render as an overlay ON TOP of a fully mounted app, so every query
 * in it fired while the user was still signed out. The owner opened the page and got a
 * console full of 404s from a surface they could not even see yet — and those 404s are
 * for endpoints that belong to the old browser-talks-to-the-edge topology and do not
 * exist here at all, so they were noise about a screen that cannot work rather than
 * evidence about the screen they were looking at.
 *
 * Holding the app back until the session is known costs one round trip and removes an
 * entire class of misleading output. A signed-out browser now makes exactly one
 * request: the one that asks whether it is signed in.
 */
export function Authed({ children }: { children: React.ReactNode }) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null)

  /*
   * One question in flight at a time.
   *
   * React's StrictMode double-invokes effects in dev, so a single load asked twice.
   * Sharing the promise makes the second caller wait for the first answer instead of
   * repeating a question the browser has already sent.
   */
  const check = async () => {
    const s = await fetchSession()
    const now = s.kind === 'in'
    setSignedIn(now)
    // Open the stream the moment we are someone, and never before. A signed-out
    // browser attempting this is refused and retried forever by the backoff.
    if (now) {
      connectStream()
      // Before any screen asks: every request below is relayed to a machine, and
      // without one chosen they all fail identically with a message about
      // configuration rather than about the account.
      void chooseRelayTarget()
      // Project Chat needs its own token + registered device + socket before the panel
      // reads anything.
      void bootstrapChatv1()
    } else {
      teardownChatv1()
    }
  }

  useEffect(() => {
    void check()
    // Any 401 anywhere means the session ended under us; ask again rather than leaving
    // a mounted app whose every button silently does nothing.
    return onSignedOut(() => void check())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // `null` is "we have not asked yet" and must not render either branch: showing the
  // gate would flash a sign-in box at someone who is signed in, and showing the app
  // would start the requests this component exists to hold back.
  if (signedIn === null) return null
  /*
   * `alreadyChecked` stops the gate re-asking what this component just asked. Without
   * it, one page load put four `/auth/me` calls in the console — two from here (React's
   * StrictMode double-invoke in dev) and two from the gate — all answering the same
   * question, all logged by the browser as errors.
   */
  if (!signedIn) return <SignInGate alreadyChecked onSignedIn={() => void check()} />
  return <>{children}</>
}
